<script lang="ts">
	import ContactFormVerification from '$lib/components/Global/ContactFormVerification.svelte';
	import Step from '$lib/components/Simulations/Components/Stepper/Step.svelte';
	import __ from '$lib/stores/TranslationStore';
	import { formatStrFloat } from '$lib/utils/helpers/moneyInput';
	import { convertToCurrency } from '$lib/utils/helpers/numbers';
	import ChoiceField from '../GenericSimulation/Fields/ChoiceField.svelte';
	import { formatStandardDate } from '../GenericSimulation/Fields/DateInput.svelte';
	import MoneyInput from '../GenericSimulation/Fields/MoneyInput.svelte';
	import Repeater from '../GenericSimulation/Fields/Repeater.svelte';
	import GenericSimulation from '../GenericSimulation/GenericSimulation.svelte';
	import ContactSubmitted from '../GenericSimulation/Result/ContactSubmitted.svelte';
	import ResultFrame from '../GenericSimulation/Result/ResultFrame.svelte';
	import SimulationCard from '../GenericSimulation/Result/SimulationCard.svelte';
	import TaxCard from '../GenericSimulation/Result/TaxCard.svelte';
	import ContactStep from '../GenericSimulation/Steps/ContactStep.svelte';
	import { type StepperStore } from '../Stepper/stepper';
	import Stepper from '../Stepper/Stepper.svelte';

	let result: Awaited<ReturnType<typeof submitSimulation>> | null = null;
	let resultVerified = false;
	let submitted = false;
	let error = false;

	let contactSubmitted = false;
	let contactDialogOpen = false;

	async function submitSimulation(data: Record<string, unknown>) {
		console.log('INCOMING DATA', data);
		const response = await fetch('/api/crm/simulations/home-credit-transfer', {
			method: 'POST',
			body: JSON.stringify({
				choice_type: data.choice_type,
				initial_value: convertToCurrency(data.initial_value as string),
				current_debt: convertToCurrency(data.current_debt as string),
				age_1: String(data.age_1 || ''),
				age_2: String(data.age_2 || ''),
				age_3: String(data.age_3 || ''),
				age_4: String(data.age_4 || ''),
				monthly_income: convertToCurrency(data.monthly_income as string),
				installment_expenses: convertToCurrency((data.installment_expenses as string) || '') || 0,
				other_expenses: convertToCurrency((data.other_expenses as string) || '') || 0,
				main_holder: {
					name: data.name,
					email: data.email,
					birthdate: formatStandardDate(data.birthdate as string),
					phone: data.phone,
					prefix: data.prefix
				},
				recieve_communications: Number(data.recieve_communications || false),
				gdpr: Number(data.accept_clause)
			})
		});

		const json = await response.json();
		console.log(json);

		if (!response.ok || !json.success) {
			throw new Error('Error submitting simulation');
		}

		if (window?.gtag) window?.gtag('event', 'Submit', { event_category: 'Sim Transferencia' });

		type TaxData = {
			spread: number;
			rate: string;
			tan: string;
			installment: string;
		};

		return json?.data?.data as {
			lead: string;
			name: string;
			simulation: {
				initial_value: string;
				current_debt: string;
				monthly_income: number;
				installment_expenses: number;
				other_expenses: number;
				var: TaxData;
				mix: TaxData;
				term: number;
			};
		};
	}

	async function handleResults(stepper: StepperStore) {
		if (submitted) return;
		submitted = true;

		simulator.doLoading(async () => {
			result = await submitSimulation(stepper.extractAllData());
            resultVerified = true;
			stepper.showResult();
		});
	}

	async function handleDirectContact(stepper: StepperStore) {
		try {
			const response = await fetch('/api/crm/leads/' + result?.lead + '/direct-contact', {
				method: 'POST'
			});
			const json = await response.json();
			contactDialogOpen = true;
			contactSubmitted = true;
		} catch (error) {
			error = true;
		}
	}

	let termChoice: ChoiceField;

	function handleRetry(stepper: StepperStore) {
		stepper.reset();
		result = null;
		resultVerified = false;
		submitted = false;
		contactSubmitted = false;
		contactDialogOpen = false;
		error = false;
	}

	let simulator: GenericSimulation;

	const choices = [
		{ label: $__('Menos de dois anos'), value: '1' },
		{ label: $__('Mais de dois anos'), value: '2' }
	];

	function ageValidation(value: unknown) {
		let val = parseInt(value as string);
		if (Object.is(val, NaN)) return;

		if (val < 18 || val > 78) {
			return $__('A idade deve estar entre 18 e 78 anos');
		}
	}
</script>

<Stepper let:stepper>
	<GenericSimulation
		title={$__('Simulador de Transferência')}
		caption={$__('Credito Habitação')}
		bind:this={simulator}
		on:retry={() => handleRetry(stepper)}
	>
		<!-- {@const test = stepper.showResult()} -->
		<Step
			title={$__('<strong>Quando</strong> contraiu o seu crédito?')}
			actionButton={false}
			displayCaption={(step) => `${choices.find((c) => c.value == step.data.choice_type)?.label}`}
		>
			<ChoiceField
				variant="grid-only"
				name="choice_type"
				bind:this={termChoice}
				placeholder={$__('Prazo')}
				on:change={(e) => {
					termChoice.safeChange(() => {
						if (termChoice.value != '') stepper.goNextStep();
					});
				}}
				options={choices}
			>
				<div
					slot="grid-option"
					let:option
					let:value
					class:bg-secondary-light={value === option.value}
					class:font-semibold={value === option.value}
					class="flex h-14 w-full items-center justify-center rounded-xl bg-inactive text-base/5 transition hover:scale-95 hover:bg-secondary-light hover:font-semibold lg:relative lg:h-[156px] lg:w-[210px] lg:text-left"
				>
					<span class="lg:absolute lg:bottom-6 lg:left-6">{@html option.label}</span>
				</div>
			</ChoiceField>
		</Step>
		<Step
			title={$__('Qual é a <strong>situação atual</strong> do crédito?')}
			validate={{
				current_debt: (value, step) => {
					const initial_value = convertToCurrency(step.data.initial_value + '');
					const current_debt = convertToCurrency(value + '');

					if (current_debt > initial_value) {
						return $__('O valor em dívida deve ser menor que o valor inicial.');
					}
				}
			}}
			displayCaption={(step) => `${$__('Valor inicial')}: <br>
				${step.data.initial_value} € <br>
				${$__('Dívida atual')}: <br>
				${step.data.current_debt} €
			`}
			required={['initial_value', 'current_debt']}
		>
			<MoneyInput
				name="initial_value"
				focusOnMount
				label={$__('Valor inicial')}
				helperText={$__('Valor de compra inicial.')}
			/>
			<MoneyInput
				name="current_debt"
				label={$__('Dívida atual')}
				helperText={$__('Valor que está em dívida ao seu banco atual.')}
			/>
		</Step>
		<Step
			title={$__(
				'<strong>Quantas pessoas</strong> vão contrair o crédito e respetivas <strong>idades?</strong>'
			)}
			validate={{
				age_1: ageValidation,
				age_2: ageValidation,
				age_3: ageValidation,
				age_4: ageValidation
			}}
			displayCaption={(step) => {
				const ages = Object.keys(step.data)
					.filter((key) => key.startsWith('age_'))
					.map((key) => step.data[key] || null)
					.filter((age) => Boolean(age));

				return ages
					.map(
						(age, i) => `${$__('Pessoa')} ${i + 1}: ${age} ${i + 1 != ages.length ? '<br>' : ''}`
					)
					.join('');
			}}
		>
			<Repeater name="age" />
		</Step>
		<Step
			title={$__('Qual o <strong>contexto financeiro mensal</strong> do seu agregado?')}
			validate={{
				monthly_income: (value) => {
					const val = convertToCurrency(value + '');
					if (val < 750) {
						return $__('O valor deve ser superior a 750,00 €');
					}
				}
			}}
			required={['monthly_income']}
			displayCaption={(step) => {
				const netSalary = formatStrFloat(step.data.monthly_income + '');
				const installmentCredits = formatStrFloat(step.data.installment_expenses + '');
				const otherExpenses = formatStrFloat(step.data.other_expenses + '');

				return `
					${$__('Rendimento líquido')}: ${netSalary ? netSalary + ' €' : 'N/A'} <br>
					${$__('Prestações com créditos')}: ${installmentCredits ? installmentCredits + ' €' : 'N/A'} <br>
					${$__('Outros gastos')}: ${otherExpenses ? otherExpenses + ' €' : 'N/A'}
				`;
			}}
		>
			<MoneyInput
				name="monthly_income"
				label={$__('Rendimento líquido')}
				helperText={$__(
					'Todos os rendimentos recebidos por mês, incluindo salário líquido, prestações de serviços, rendas de imóveis, dividendos, ou investimentos de quem vai contratar o crédito.'
				)}
			/>
			<MoneyInput
				name="installment_expenses"
				label={$__('Prestações com créditos')}
				helperText={$__(
					'Todas as despesas mensais com prestações de outros créditos. Este valor não deve ultrapassar 30% dos rendimentos, mas pode ser aceite até 40%.'
				)}
			/>
			<MoneyInput
				name="other_expenses"
				label={$__('Outros gastos')}
				helperText={$__(
					'Todas as despesas mensais com prestações de outros créditos. Este valor não deve ultrapassar 30% dos rendimentos, mas pode ser aceite até 40%.'
				)}
			/>
		</Step>
		<ContactStep on:complete={() => handleResults(stepper)} exceptFields={['nif']} />
		<div slot="content" class="flex flex-col justify-center">
			{#if error}
				<p>Error</p>
				<!-- <div class="-mt-4 h-full w-full">
					<SimulationError on:retry={() => handleRetry(stepper)} />
				</div> -->
			{/if}
			{#if result}
				{@const resultData = result.simulation}
				{#if resultVerified}
					{#if contactDialogOpen}
						<ContactSubmitted on:close={() => (contactDialogOpen = false)} />
					{/if}
					<ResultFrame
						clientName={result.name}
						on:retry={() => handleRetry(stepper)}
						directContactRequested={contactSubmitted}
						on:contact={() => handleDirectContact(stepper)}
					>
						<div class="grid grid-cols-2 grid-rows-1 gap-3 md:gap-4 lg:gap-6">
							<TaxCard
								class="lg:px-5 lg:py-6"
								amount={parseFloat(resultData.var.installment)}
								title={$__('Taxa variável')}
								caption="EURIBOR 3M"
								let:integerPart
								let:decimalPart
								let:bottomText
							>
								<svelte:fragment>
									<div class="flex flex-col gap-3">
										<div class="flex justify-start gap-1">
											<em class="text-5xl not-italic tracking-tighter sm:text-5xl lg:text-[75px]"
												>{integerPart.toLocaleString('pt-BR')}</em
											>
											<span class="text-xl sm:text-2xl lg:mt-[5px] lg:text-[30px]">
												,{decimalPart}
											</span>
										</div>
										<p class="text-xs font-semibold uppercase tracking-widest lg:text-base">
											{bottomText}
										</p>
									</div>
								</svelte:fragment>
								<ul class="flex h-full flex-col justify-center gap-5" slot="values">
									<li>
										<div class="whitespace-nowrap">
											<p class="text-xs">EURIBOR 3M</p>
											<p class="font-semibold">{parseFloat(resultData.var.rate).toFixed(2)} %</p>
										</div>
									</li>
									<li>
										<div class="whitespace-nowrap">
											<p class="text-xs">Spread</p>
											<p class="font-semibold">{resultData.var.spread.toFixed(2)} %</p>
										</div>
									</li>
									<li>
										<div class="whitespace-nowrap">
											<p class="text-xs">TAN</p>
											<p class="font-semibold">{parseFloat(resultData.var.tan).toFixed(2)} %</p>
										</div>
									</li>
								</ul>
							</TaxCard>
							<TaxCard
								class="lg:px-5 lg:py-6"
								amount={parseFloat(resultData.mix.installment)}
								title={$__('Taxa mista')}
								baseColor="secondary"
								let:integerPart
								let:decimalPart
								let:bottomText
							>
								<svelte:fragment>
									<div class="flex flex-col gap-3">
										<div class="flex justify-start gap-1">
											<em class="text-5xl not-italic tracking-tighter sm:text-5xl lg:text-[75px]"
												>{integerPart.toLocaleString('pt-BR')}</em
											>
											<span class="text-xl sm:text-2xl lg:mt-[5px] lg:text-[30px]">
												,{decimalPart}
											</span>
										</div>
										<p class="text-xs font-semibold uppercase tracking-widest lg:text-base">
											{bottomText}
										</p>
									</div>
								</svelte:fragment>
								<ul class="flex h-full flex-col justify-center gap-5" slot="values">
									<li>
										<div class="whitespace-nowrap">
											<p class="text-xs">{$__('Taxa mista')}</p>
											<p class="font-semibold">{parseFloat(resultData.mix.rate).toFixed(2)} %</p>
										</div>
									</li>
									<li>
										<div class="whitespace-nowrap">
											<p class="text-xs">Spread</p>
											<p class="font-semibold">{resultData.mix.spread.toFixed(2)} %</p>
										</div>
									</li>
									<li>
										<div class="whitespace-nowrap">
											<p class="text-xs">TAN</p>
											<p class="font-semibold">{parseFloat(resultData.mix.tan).toFixed(2)} %</p>
										</div>
									</li>
								</ul>
							</TaxCard>
							<!-- <SimulationCard
								items={[
									{ label: $__('Valor a financiar'), value: `${result.current_debt}€` },
									{ label: $__('Valor do imóvel'), value: `${result.initial_value}€` },
									{ label: $__('Prazo'), value: `${result.term} ${$__('anos')}` },
									{ label: 'Euribor 3M', value: `${parseFloat(result.euribor).toFixed(2)} %` },
									{
										label: $__('Spread a partir de'),
										value: `${result.spread.toFixed(2)} %`
									},
									{ label: 'TAN', value: `${result.tan.toFixed(2)} %` }
								]}
							/> -->
							<!-- <SimulationCard
								items={[
									{ label: $__('Valor a financiar'), value: `${result.current_debt}€` },
									{ label: $__('Valor do imóvel'), value: `${result.initial_value}€` },
									{ label: $__('Prazo'), value: `${result.term} ${$__('anos')}` },
									{
										label: $__('Taxa mista a partir de'),
										value: `${parseFloat(result.mixed_rate).toFixed(2)} %`
									},
									{
										label: $__('Spread a partir de'),
										value: `${result.spread_mix.toFixed(2)} %`
									},
									{ label: 'TAN', value: `${parseFloat(result.tan_mix).toFixed(2)} %` }
								]}
							/> -->
							<SimulationCard
								class="col-span-full"
								items={[
									{ label: $__('Valor a financiar'), value: `${resultData.current_debt}€` },
									{ label: $__('Valor do imóvel'), value: `${resultData.initial_value}€` },
									{ label: $__('Prazo'), value: `${resultData.term} ${$__('anos')}` }
								]}
								let:items
							>
								<svelte:fragment>
									<ul
										class="flex w-full flex-wrap items-end justify-between gap-x-10 gap-y-5 divide-opacity-20 self-center px-5 md:px-7"
									>
										{#each items as item}
											<li class="flex justify-center">
												<div class="whitespace-nowrap">
													<p class="text-xs">{item.label}</p>
													<p class="font-semibold">{item.value}</p>
												</div>
											</li>
										{/each}
									</ul>
								</svelte:fragment>
							</SimulationCard>
						</div>
					</ResultFrame>
				{:else}
					<div class="self-center">
						<ContactFormVerification
							on:success={() => {
								resultVerified = true;
							}}
							verificationId={result?.lead}
						/>
					</div>
				{/if}
			{/if}
		</div>
	</GenericSimulation>
</Stepper>
